import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { clientApi, type ChatMessage as PortalChatMessage, type ChatStreamEvent, type ChatToolCall, type ConversationSummary } from '../api/client';
import { ChatMessage } from './ChatMessage';

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const CHAT_PANEL_STYLES = `
  .chat-panel-shell {
    width: min(400px, calc(100vw - 1rem));
  }

  .chat-panel-textarea {
    min-height: 88px;
    max-height: 160px;
  }

  @media (max-width: 640px) {
    .chat-panel-shell {
      width: 100vw;
    }
  }
`;

export function ChatPanel({ isOpen, onClose }: ChatPanelProps) {
  const [messages, setMessages] = useState<PortalChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && !showHistory) {
      textareaRef.current?.focus();
    }
  }, [isOpen, showHistory]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const result = await clientApi.listConversations(20);
      setConversations(result.conversations);
    } catch {
      // Persistence may be disabled — just show empty list
      setConversations([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  function handleShowHistory() {
    setShowHistory(true);
    void loadHistory();
  }

  async function handleSelectConversation(id: string) {
    try {
      const result = await clientApi.getConversation(id);
      setMessages(result.messages);
      setConversationId(result.conversationId);
      setShowHistory(false);
    } catch {
      // If the conversation can't be loaded, just stay on the list
    }
  }

  function handleNewChat() {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
    setInputText('');
  }

  async function submitMessage() {
    const trimmedInput = inputText.trim();
    if (!trimmedInput || isLoading) {
      return;
    }

    const nextMessages: PortalChatMessage[] = [
      ...messages,
      { role: 'user', content: trimmedInput },
    ];

    setMessages(nextMessages);
    setInputText('');
    setIsLoading(true);

    const abortController = new AbortController();
    abortRef.current = abortController;

    let streamedContent = '';
    const streamedToolCalls: ChatToolCall[] = [];

    try {
      // Add a placeholder assistant message that we'll update as deltas arrive
      setMessages([...nextMessages, { role: 'assistant', content: '' }]);

      await clientApi.streamChatMessage(
        nextMessages,
        (event: ChatStreamEvent) => {
          switch (event.type) {
            case 'delta':
              streamedContent += event.content;
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, content: streamedContent, toolCalls: streamedToolCalls.length > 0 ? [...streamedToolCalls] : undefined };
                }
                return updated;
              });
              break;
            case 'tool_result':
              streamedToolCalls.push({ toolName: event.toolName, args: {}, result: event.result });
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, content: streamedContent, toolCalls: [...streamedToolCalls] };
                }
                return updated;
              });
              break;
            case 'done':
              setConversationId(event.conversationId);
              setMessages([
                ...nextMessages,
                { role: 'assistant', content: event.reply, toolCalls: event.toolCalls },
              ]);
              break;
            case 'error':
              setMessages([
                ...nextMessages,
                { role: 'assistant', content: `I ran into an error: ${event.message}` },
              ]);
              break;
          }
        },
        abortController.signal,
        conversationId ?? undefined,
      );
    } catch (error) {
      if (abortController.signal.aborted) return;
      const errorMessage = error instanceof Error ? error.message : 'Unable to send message.';
      setMessages([
        ...nextMessages,
        { role: 'assistant', content: `I ran into an error while processing that request: ${errorMessage}` },
      ]);
    } finally {
      abortRef.current = null;
      setIsLoading(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitMessage();
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submitMessage();
    }
  }

  const isSubmitDisabled = isLoading || inputText.trim().length === 0;

  return (
    <>
      <style>{CHAT_PANEL_STYLES}</style>

      <div
        className={clsx(
          'fixed inset-0 z-50 transition-colors duration-200',
          isOpen ? 'pointer-events-auto bg-slate-900/30' : 'pointer-events-none bg-slate-900/0',
        )}
        aria-hidden={!isOpen}
      >
        <div className="absolute inset-0" onClick={onClose} />

        <aside
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-panel-title"
          className={clsx(
            'chat-panel-shell absolute inset-y-0 right-0 flex h-full flex-col border-l border-gray-200 bg-gray-50 shadow-2xl transition-transform duration-200 ease-out',
            isOpen ? 'translate-x-0' : 'translate-x-full',
          )}
          onClick={(event) => event.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4">
            <div className="flex-1 min-w-0">
              <h2 id="chat-panel-title" className="text-lg font-semibold text-gray-900">
                Portal Assistant
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Ask about templates, retailers, or label selection.
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleNewChat}
                className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                aria-label="New chat"
                title="New chat"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </button>
              <button
                type="button"
                onClick={showHistory ? () => setShowHistory(false) : handleShowHistory}
                className={clsx(
                  'rounded-md p-2 transition-colors',
                  showHistory
                    ? 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                )}
                aria-label={showHistory ? 'Back to chat' : 'Chat history'}
                title={showHistory ? 'Back to chat' : 'Chat history'}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close chat panel"
              >
                X
              </button>
            </div>
          </div>

          {showHistory ? (
            /* History list */
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Recent Conversations</h3>
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="btn btn-primary text-xs px-3 py-1.5"
                >
                  + New Chat
                </button>
              </div>

              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-6 text-center text-sm text-gray-500">
                  No prior conversations found.
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.map((conv) => (
                    <button
                      key={conv.conversationId}
                      type="button"
                      onClick={() => void handleSelectConversation(conv.conversationId)}
                      className={clsx(
                        'w-full rounded-xl border bg-white px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50',
                        conversationId === conv.conversationId ? 'border-primary-400 bg-primary-50' : 'border-gray-200',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">
                          {conv.messageCount} messages
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(conv.updatedAt)}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-sm text-gray-700">
                        {conv.title}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Chat messages */
            <>
              <div className="flex-1 overflow-y-auto px-4 py-4" aria-busy={isLoading}>
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
                      Start a conversation to search retailer requirements, compare templates, or validate
                      label choices.
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <ChatMessage key={`${message.role}-${index}`} message={message} />
                    ))
                  )}

                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="inline-flex items-center gap-3 rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 shadow-sm">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
                        <span>Thinking...</span>
                      </div>
                    </div>
                  )}

                  <div ref={messageEndRef} />
                </div>
              </div>

              <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-white px-4 py-4">
                <label htmlFor="chat-panel-input" className="sr-only">
                  Chat message
                </label>
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
                  <textarea
                    id="chat-panel-input"
                    ref={textareaRef}
                    value={inputText}
                    onChange={(event) => setInputText(event.target.value)}
                    onKeyDown={handleInputKeyDown}
                    placeholder="Ask a question about your labels..."
                    className="chat-panel-textarea w-full resize-none border-0 bg-transparent px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                    disabled={isLoading}
                  />

                  <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-3 py-3">
                    <span className="text-xs text-gray-500">Enter to send, Shift+Enter for newline</span>
                    <button
                      type="submit"
                      className={clsx(
                        'btn btn-primary inline-flex items-center justify-center min-w-[92px]',
                        isSubmitDisabled && 'cursor-not-allowed opacity-50',
                      )}
                      disabled={isSubmitDisabled}
                    >
                      {isLoading ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                          <span>Sending</span>
                        </span>
                      ) : (
                        'Send'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </aside>
      </div>
    </>
  );
}

function formatRelativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(isoDate).toLocaleDateString();
}
